import { useCallback, useEffect, useRef, useState } from 'react';

import { SPEED_TO_LEAD_RECORDING_DOWNLOAD_TIMEOUT_MS } from './constants';

const useSeekableRecording = (source: string) => {
  const [hasSeekError, setHasSeekError] = useState(false);
  const downloadRef = useRef<Promise<string> | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const disposedRef = useRef(false);

  useEffect(() => {
    disposedRef.current = false;
    setHasSeekError(false);
    return () => {
      disposedRef.current = true;
      controllerRef.current?.abort();
      controllerRef.current = null;
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
      downloadRef.current = null;
    };
  }, [source]);

  const prepareRecording = useCallback(() => {
    if (downloadRef.current) return downloadRef.current;
    const controller = new AbortController();
    controllerRef.current = controller;
    const timeout = window.setTimeout(() => controller.abort(), SPEED_TO_LEAD_RECORDING_DOWNLOAD_TIMEOUT_MS);
    downloadRef.current = (async () => {
      try {
        const response = await fetch(source, { signal: controller.signal });
        if (!response.ok) throw new Error(`Unable to load recording: ${response.status}`);
        const blob = await response.blob();
        if (controller.signal.aborted || disposedRef.current) throw new Error('Recording download cancelled');
        const url = URL.createObjectURL(blob);
        objectUrlRef.current = url;
        return url;
      } catch (error) {
        if (!disposedRef.current && controllerRef.current === controller) setHasSeekError(true);
        throw error;
      } finally {
        window.clearTimeout(timeout);
      }
    })();
    return downloadRef.current;
  }, [source]);

  return { prepareRecording, hasSeekError };
};

export default useSeekableRecording;
